import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  OutputDataConfirmationType,
  OutputDescriptionProcessingStatesParams,
  Padding,
  TextEllipsis,
  useOutputDataConfirmationsQuery,
  formatCodeName,
  formatDateTimeShort,
  getUserName,
  ISODate,
  getDayjs,
  ISO_DATE_FORMAT,
  FormLabel,
  useMutateOutputDataConfirmations,
  useAlertContext,
  useMutationErrors,
  OutputDataConfirmationsMutationType,
  Form,
  OutputDataConfirmationsMutationSchema,
  TanstackFormTable,
  ActionButton,
  PeriodEndPicker,
  useSystemClassifierItemById,
  Frequency,
  useUserInfo,
  FormField,
  getFormTableFieldName,
  Detail,
} from '@atlas-frontend/sdk';
import translations from './ConfirmationsModal.translations.json';
import { FC, useCallback, useMemo, useState } from 'react';
import { Flex } from 'antd';
import { createColumnHelper } from '@tanstack/react-table';
import { VersionCell } from '../VersionCell';

const { addTranslations, t } = i18nNsFactory('output-data-confirmations');
addTranslations(translations);

export interface ConfirmationsModalProps {
  params: OutputDescriptionProcessingStatesParams;
  onClose: () => void;
}

export const ConfirmationsModal: FC<ConfirmationsModalProps> = (props) => {
  const { params, onClose } = props;
  const { frequencyId } = params;
  const frequency = useSystemClassifierItemById('FREQUENCY', frequencyId)
    ?.code as Frequency;
  const [valueDate, setValueDate] = useState<ISODate>(params.valueDate);

  const { data, isLoading } = useOutputDataConfirmationsQuery({
    ...params,
    valueDate,
  });

  const { mutateAsync } = useMutateOutputDataConfirmations();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors();
  const { resetAlert, setSuccess } = useAlertContext();

  const handleSubmit = useCallback(
    (data: OutputDataConfirmationsMutationType) => {
      resetAlert();
      clearMutationErrors();
      return mutateAsync({
        id: 1, // Dummy id
        data,
      })
        .then(() => {
          onClose();
          setSuccess();
        })
        .catch((error) => {
          setMutationErrors(error?.response?.data?.errors ?? []);
        });
    },
    [
      clearMutationErrors,
      mutateAsync,
      onClose,
      resetAlert,
      setMutationErrors,
      setSuccess,
    ],
  );

  if (!data) return null;

  return (
    <Form
      schema={OutputDataConfirmationsMutationSchema}
      onSubmit={handleSubmit}
      serverErrors={mutationErrors}
      defaultValues={{
        groupId: data.groupId,
        confirmations: data.confirmations,
      }}
    >
      <Modal
        onCancel={onClose}
        width="60rem"
        loading={isLoading}
        footer={
          <ModalFooter>
            <Flex gap="small" justify="end">
              <ActionButton type="abort" onClick={onClose} />
              <ActionButton type="formSubmit" />
            </Flex>
          </ModalFooter>
        }
      >
        <ModalHeader>
          <ModalTitle>
            <TextEllipsis>
              {t('title', { count: data.confirmations.length })}
            </TextEllipsis>
          </ModalTitle>
        </ModalHeader>
        <Padding scrollable>
          <Flex vertical gap="middle">
            <Detail label={t('group')}>
              {`${data?.groupCode} - ${data.groupName}`}
            </Detail>
            <Flex vertical gap="small" style={{ width: '10rem' }}>
              <FormLabel htmlFor="valueDate">{t('valueDate')}</FormLabel>
              <PeriodEndPicker
                id="valueDate"
                value={valueDate ? getDayjs(valueDate, ISO_DATE_FORMAT) : null}
                onChange={(date) =>
                  date
                    ? setValueDate(date.format(ISO_DATE_FORMAT) as ISODate)
                    : null
                }
                frequency={frequency}
                allowClear={false}
              />
            </Flex>
          </Flex>
          <Padding top>
            <ConfirmationsEditTable />
          </Padding>
        </Padding>
      </Modal>
    </Form>
  );
};

const columnHelper = createColumnHelper<OutputDataConfirmationType>();

export const ConfirmationsEditTable: FC = () => {
  const { user } = useUserInfo();
  const columns = useMemo(
    () => [
      columnHelper.accessor((rowData) => formatCodeName(rowData), {
        id: 'name',
        header: t('column.name'),
        meta: { minWidth: '16ch' },
      }),
      columnHelper.accessor('versionId', {
        header: t('column.version'),
        cell: (props) => (
          <VersionCell
            version={props.row.original.versionNumber}
            versionStatus={props.row.original.versionStatus}
          />
        ),
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          width: '20ch',
        },
      }),
      columnHelper.accessor('confirmedAt', {
        header: t('column.confirmedAt'),
        cell: (props) => formatDateTimeShort(props.getValue()),
        enableColumnFilter: false,
        enableSorting: false,
        meta: { width: '20ch' },
      }),
      columnHelper.accessor('comment', {
        header: t('column.comment'),
        cell: (props) => {
          const { confirmedBy } = props.row.original;
          return (
            <FormField
              size="small"
              name={getFormTableFieldName(props)}
              preview={{
                previewOnly: user.id !== confirmedBy.id,
              }}
            />
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
        meta: { minWidth: '20ch' },
      }),
      columnHelper.accessor((rowData) => getUserName(rowData.confirmedBy), {
        id: 'confirmedBy',
        header: t('column.confirmedBy'),
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          width: '16ch',
        },
      }),
    ],
    [user.id],
  );

  return (
    <TanstackFormTable
      name="confirmations"
      columns={columns}
      defaultSort={[{ id: 'name', desc: false }]}
      persistColumnStyle={false}
    />
  );
};
